export type ProcurementRoleName =
  | 'SUPER_ADMIN'
  | 'COMPANY_ADMIN'
  | 'COMPANY_GROUP_ADMIN'
  | 'OWNER_APPROVER'
  | 'CREW'
  | 'VENDOR'
  | 'SHORE'
  | 'OPERATOR'
  | 'UNKNOWN'

const KNOWN_ROLES: ProcurementRoleName[] = [
  'SUPER_ADMIN',
  'COMPANY_ADMIN',
  'COMPANY_GROUP_ADMIN',
  'OWNER_APPROVER',
  'CREW',
  'VENDOR',
  'SHORE',
  'OPERATOR',
]

const normalizeRoleValue = (value: unknown): string => {
  if (!value) return ''
  return String(value).trim().toUpperCase().replace(/^ROLE_/, '')
}

export const resolveProcurementRoleName = (
  currentUser?: any,
  auth?: any
): ProcurementRoleName => {
  const candidates = [
    currentUser?.role?.roleType,
    currentUser?.role?.roleName,
    auth?.role?.roleName,
    auth?.role?.roleType,
    auth?.userDetails?.roleName,
    auth?.userDetails?.role?.roleName,
    auth?.userDetails?.role?.roleType,
    currentUser?.authorities?.[0]?.authority,
  ]

  for (const candidate of candidates) {
    const normalized = normalizeRoleValue(candidate)
    if (KNOWN_ROLES.includes(normalized as ProcurementRoleName)) {
      return normalized as ProcurementRoleName
    }
  }

  // Legacy fallback by id only when role name/type is not available.
  const fallbackId = Number(
    currentUser?.role?.id ?? auth?.role?.id ?? currentUser?.roleId ?? auth?.userDetails?.roleId ?? NaN
  )
  switch (fallbackId) {
    case 1:
      return 'SUPER_ADMIN'
    case 3:
      return 'COMPANY_ADMIN'
    case 4:
      return 'CREW'
    case 5:
      return 'COMPANY_GROUP_ADMIN'
    case 6:
      return 'OPERATOR'
    case 8:
      return 'OWNER_APPROVER'
    case 7:
      return 'VENDOR'
    default:
      return 'UNKNOWN'
  }
}

export const hasAnyProcurementRole = (
  role: ProcurementRoleName,
  ...allowed: ProcurementRoleName[]
): boolean => allowed.includes(role)

/** Display name for PO Raised By / similar fields (company vs operator vs superadmin). */
export const resolveProcurementLoggedInDisplayName = (currentUser?: any, auth?: any): string => {
  const role = resolveProcurementRoleName(currentUser, auth)
  if (role === 'SUPER_ADMIN') return 'Superadmin'

  const u = currentUser || {}
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()

  if (role === 'COMPANY_GROUP_ADMIN') {
    return (
      u.companyGroupAdmin?.name ||
      u.vessel?.companyGroupAdmin?.name ||
      u.company?.cgaid?.name ||
      fullName ||
      u.username ||
      'Company'
    )
  }
  if (role === 'COMPANY_ADMIN') {
    return (
      u.companyAdmin?.name ||
      u.company?.name ||
      (u.company as {caid?: {name?: string}} | undefined)?.caid?.name ||
      fullName ||
      u.username ||
      'Sub Company'
    )
  }
  if (role === 'OPERATOR' || role === 'SHORE' || role === 'OWNER_APPROVER') {
    return fullName || u.username || role.replace(/_/g, ' ')
  }
  return fullName || u.username || ''
}
